// Component that displays FAST schedule.
import React, { useEffect, useState, useRef } from 'react';

type fastScheduleShows = {
  title: string;
  startTime: string;
  endTime: string;
  longDescription: string;
  startPretty: string;
  duration: number;
};

type FastScheduleProps = {
  fastSchedule: fastScheduleShows[];
  setFastMeta: ({
    fastHeroTitle,
    fastHeroDek,
  }) => void;
}

export default function FastSchedule({
  fastSchedule,
  setFastMeta,
}: FastScheduleProps) {
  const checkScreenDimensions = (multiple, showWidth) => ({
    multiple,
    showWidth,
  });

  const carouselRef = useRef<HTMLInputElement>(null);

  /**
   * Media Queries to be listened to for altering Carousel Settings
   */
  const mediaQueries = [
    window.matchMedia('screen and (max-width: 767px)'),
    window.matchMedia('screen and (min-width: 768px) and (max-width: 1023px)'),
    window.matchMedia('screen and (min-width: 1024px) and (max-width: 1279px)'),
    window.matchMedia('screen and (min-width: 1280px)'),
  ];

  /**
   *  Carousel Settings, corresponding to the indexes of mediaQueries above. First index is Amount of slides to scroll. Second index is Width in pixels to scroll by
   */
  const scheduleSettings = [
    [0, 0],
    [3, 202],
    [4, 240],
    [5, 240],
  ];

  /**
   *  Determine what breakpoint we are currently in for initial State
   */
  const matchingIndex = scheduleSettings[mediaQueries.findIndex((mq) => mq.matches)];

  const [
    multipleSetting,
    widthSetting,
  ] = matchingIndex;

  /**
   * Helper to determine if the show starts within 12 hours of NOW
   *
   * @param {startTime} Date string of the start time of show
   * @returns {Boolean}
   */
  const limitTwelveHours = ({
    startTime,
  }) => (Date.now() + 43200000) > Date.parse(startTime);

  /**
   * Helper to determine if the show has ended
   *
   * @param {endTime} Date string of the end time of show
   * @returns {Boolean}
   */
  const checkCurrent = ({
    endTime,
  }) => (Date.now() < Date.parse(endTime));

  /**
   * Helper to return progress percentage of current Show
   *
   * @param startTime Date string of the start time of show
   * @param duration Number Length of show in milliseconds
   * @returns {Number}
   */
  const checkProgress = (startTime, duration) => Math.round(((Date.now() - Date.parse(startTime)) / duration) * 100);

  const [currentData, setCurrentData] = useState(fastSchedule.filter(checkCurrent));
  const [limitedData, setLimitedData] = useState(currentData.filter(limitTwelveHours));
  const [progressPercentage, setProgressPercentage] = useState((() => {
    const [
      {
        startTime,
        duration,
      },
    ] = limitedData;
    return checkProgress(startTime, duration * 60000);
  })());
  const [currentIndex, setCurrentIndex] = useState(0);
  const [carouselParams, setCarouselParams] = useState(checkScreenDimensions(multipleSetting, widthSetting));
  const [dragXStartPos, setDragXStartPos] = useState(0);

  /**
   *  If breakpoints change, alter Carousel settings
   */
  const handleResize = (index) => {
    const [
      multiple,
      showWidth,
    ] = scheduleSettings[index];

    const dimensions = checkScreenDimensions(multiple, showWidth);

    setCarouselParams({
      ...dimensions,
    });
  };

  const {
    multiple,
    showWidth,
  } = carouselParams;

  useEffect(() => {
    /**
     *  Listening to media query breakpoints in mediaQueries array
     */
    mediaQueries.map((query, i) => {
      query.addListener((e) => {
        if (e.matches) {
          handleResize(i);
        }
      });

      return true;
    });

    /**
     *  If user leaves window open for a long time and comes back, remove old shows
     */
    window.addEventListener('focus', () => {
      setCurrentData(currentData.filter(checkCurrent));
    });
  }, []);

  useEffect(() => {
    const [
      currentShow,
    ] = currentData;

    const {
      startTime,
      title,
      longDescription,
      duration,
    } = currentShow;

    /**
     *  Every second determine how much progress has been made in the current show. and if show is over, remove it
     */
    const intervalId = setInterval(() => {
      if (! checkCurrent(currentShow)) {
        setCurrentData(currentData.slice(1));
      }

      setProgressPercentage(checkProgress(startTime, duration * 60000));
    }, 1000);

    /**
     *  Hook to update Title and Dek in Video hero component
     */
    setFastMeta({
      fastHeroTitle: (- 1 < title.indexOf(': ') ? title.split(': ')[1] : title),
      fastHeroDek: longDescription,
    });

    /**
     *  Every time Show is list is updated, filter out shows that are more than 12 hours in the future
     */
    setLimitedData(currentData.filter(limitTwelveHours));

    return () => clearInterval(intervalId);
  }, [currentData]);

  /**
   *  When carousel settings change, set carousel index to the back to the beginning
   */
  useEffect(() => {
    setCurrentIndex(0);
    carouselRef.current?.scroll?.(0, 0);
  }, [carouselParams]);

  const shiftCarousel = (shiftAmount) => {
    let newIndex = currentIndex + shiftAmount;
    if (0 > newIndex) {
      newIndex = 0;
    } else if (newIndex > limitedData.length - Math.abs(shiftAmount)) {
      newIndex = limitedData.length - Math.abs(shiftAmount);
    }

    setCurrentIndex(newIndex);
  };

  /**
   *  WIP, fast-schedule__container needs 'draggable' attribute to activate
   */
  const handleDragStart = (event) => {
    setDragXStartPos(event.clientX);
  };

  /**
   *  WIP, fast-schedule__container needs 'draggable' attribute to activate
   */
  const handleDragEnd = (event) => {
    const {
      clientX,
    } = event;

    shiftCarousel(clientX > dragXStartPos ? - multiple : multiple);
  };

  const {
    wp: {
      i18n: {
        __,
      },
    },
  } = window;

  const onNowText = __('on now', 'nbc');

  return (
    <div className="fast-schedule">
      {0 < currentIndex &&
        <button
          className="fast-schedule__back"
          type="button"
          onClick={() => shiftCarousel(- multiple)}
        >
          Back
        </button>
      }
      {(multiple < limitedData.length && limitedData.length - multiple > currentIndex) &&
        <button
          className="fast-schedule__forward"
          type="button"
          onClick={() => shiftCarousel(multiple)}
        >
          Forward
        </button>
      }
      <div className="fast-schedule__carousel" ref={carouselRef}>
        <div
          className="fast-schedule__container"
          style={{ transform: `translateX(${- currentIndex * showWidth}px)` }}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          { limitedData && limitedData.map((show, index) => {
            const {
              title,
              startPretty,
            } = show;

            const formattedTitle = (- 1 < title.indexOf(': ') ? title.split(': ')[1] : title);

            return (
              <div className={`fast-show ${0 === index ? 'fast-show__onnow' : ''}`}>
                {0 === index &&
                  <div className="fast-show__progressContainer">
                    <span className="fast-show__progress" style={{ left: `${progressPercentage}%` }} />
                  </div>
                }
                <div className="fast-show__time">
                  {0 === index ? onNowText : startPretty}
                </div>
                <div className="fast-show__title">
                  {formattedTitle}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
